<template>
  <div
    v-if="callCentersAllowed?.length"
    class="agent-dashboard"
  >
    <div class="agent-dashboard__topbar">
      <div>
        <div class="text-sm text-secondary">Call agent dashboard</div>
        <h1 class="mt-1 text-3xl font-semibold md:text-5xl">Welcome in, {{ firstName }}</h1>
      </div>

      <div class="agent-dashboard__filters">
        <template v-if="$can('manage_call_center_team')">
          <Select
            v-model="callCenterSelected"
            :options="callCentersAllowed"
            icon="headset"
            value-key="id"
            label-key="name"
            :placeholder="`Select a Call Center`"
            class="min-w-56 shrink-0"
            :readonly="callCentersAllowed?.length == 1"
            @select="updateCallCenterSelected"
          />
          <Select
            v-model="agentSelected"
            v-tippy="!callCenterSelected ? 'Select a call center' : ''"
            mode="single"
            :close-on-select="true"
            :searchable="true"
            :options="callAgents"
            label-key="fullName"
            value-key="id"
            class="min-w-56 shrink-0"
            :icon="agentSelected ? null : 'user'"
            placeholder="Select an Agent"
            :disabled="!callCenterSelected"
            @select="onAgentChange()"
          >
            <template #value="{ value }">
              <div class="flex items-center">
                <Avatar
                  :url="value.profilePicUrl"
                  :text="value.initials"
                  size="xs"
                  class="mr-2"
                />
                {{ value.fullName }}
              </div>
            </template>

            <template #option="{ option }">
              <div class="flex items-center">
                <Avatar
                  :url="option.profilePicUrl"
                  :text="option.initials"
                  size="xs"
                  class="mr-2"
                />
                {{ option.fullName }}
              </div>
            </template>
          </Select>
        </template>

        <DatePicker
          v-model="selectedDate"
          class="min-w-48 shrink-0"
          selection-mode="single"
          :months-visible="1"
          :max-date="monthEnd"
          close-on-select
          @select="onDateChange"
        />
      </div>
    </div>

    <div class="agent-dashboard__hero">
      <div class="agent-profile-card">
        <div class="agent-profile-card__photo">
          <Avatar
            :url="selectedAgentProfile?.profilePicUrl"
            :text="selectedAgentProfile?.initials ?? authStore.details.initials"
            size="xl"
            no-border
          />
        </div>
        <div class="agent-profile-card__content">
          <div class="text-2xl font-semibold">{{ selectedAgentName }}</div>
          <div class="mt-1 text-sm text-white/80">{{ dayjs(selectedDate).format('dddd, D MMMM') }}</div>
          <div class="agent-profile-card__shifts">
            <div class="mb-2 text-xs uppercase tracking-[0.14em] text-white/60">{{ shiftLabel }}</div>
            <Skeleton
              v-if="dataIsLoading"
              height="3rem"
              width="100%"
            />
            <div
              v-else-if="filteredShifts.length > 0"
              class="flex flex-col gap-2"
            >
              <div
                v-for="shift in filteredShifts"
                :key="shift.id"
                class="agent-profile-shift"
              >
                <div class="agent-profile-shift__bar"></div>
                <div>
                  <div class="font-semibold">
                    {{ dayjs(shift.start, 'HH:mm:ss').format('HH:mm') }} - {{ dayjs(shift.end, 'HH:mm:ss').format('HH:mm') }}
                  </div>
                  <div class="text-xs text-white/70">
                    {{ shift.productiveHours }} productive hrs &middot; {{ shift.callTarget * shift.productiveHours }} calls &middot;
                    {{ shift.appointmentTarget * shift.productiveHours }} appts
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-sm text-white/70"
            >
              {{ noShiftMessage }}
            </div>
          </div>
        </div>
      </div>

      <div class="agent-overview-card">
        <div class="mb-6 flex items-start justify-between">
          <div>
            <div class="text-lg font-semibold">Progress</div>
            <div class="mt-2 text-4xl font-semibold">{{ filteredStats.today.productiveHours }}h</div>
            <div class="text-sm text-secondary">{{ progressSubtitle }}</div>
          </div>
          <Icon
            type="arrow-up-right-from-square"
            class="text-secondary"
          />
        </div>
        <div class="grid grid-cols-3 gap-3">
          <div
            v-for="metric in selectedDayStatCards"
            :key="metric.label"
            class="agent-mini-stat"
          >
            <Avatar
              :icon="metric.icon"
              :color="metric.color"
              size="sm"
              no-border
            />
            <div class="mt-3 text-2xl font-semibold">{{ metric.value }}</div>
            <div class="text-xs text-secondary">{{ metric.label }}</div>
          </div>
        </div>
      </div>

      <div class="agent-kpi-card">
        <div class="mb-3 flex items-center justify-between">
          <div>
            <div class="text-lg font-semibold">{{ dailyKpiTitle }}</div>
            <div class="text-sm text-secondary">{{ selectedDateLongLabel }}</div>
          </div>
          <ScoreBadge
            v-model="computedData.badges.streak"
            badge="streak"
          />
        </div>
        <KpiRings
          v-model="computedData.kpiRings"
          class="max-h-[210px]"
          :hover-stats="true"
          size="lg"
        />
      </div>
    </div>

    <div class="agent-dashboard__grid">
      <section class="agent-card agent-card--wide">
        <div class="agent-card__header">
          <div>
            <div class="text-lg font-semibold">Performance rhythm</div>
            <div class="text-sm text-secondary">{{ performanceRhythmSubtitle }}</div>
          </div>
        </div>

        <div v-if="dataIsLoading">
          <Skeleton
            v-for="index in 3"
            :key="index"
            height="4rem"
            width="100%"
            class="mb-2"
          />
        </div>
        <div
          v-else
          class="agent-period-list"
        >
          <div
            v-for="period in periodPerformanceRows"
            :key="period.label"
            class="agent-period-row"
          >
            <div>
              <div class="font-semibold">{{ period.label }}</div>
              <div class="text-sm text-secondary">{{ period.productiveHours }} productive hours</div>
            </div>
            <div class="agent-period-row__stats">
              <div>
                <span>{{ period.calls }}</span>
                <small>/ {{ period.callTarget }} calls</small>
              </div>
              <div>
                <span>{{ period.appointments }}</span>
                <small>/ {{ period.appointmentTarget }} appts</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="agent-card">
        <div class="agent-card__header">
          <div>
            <div class="text-lg font-semibold">Tasks</div>
            <div class="text-sm text-secondary">{{ taskPressureSubtitle }}</div>
          </div>
          <Button
            severity="primary"
            text
            label="View"
            size="xs"
            router-link="/tasks"
          />
        </div>
        <div class="mt-4 flex flex-col gap-3">
          <div
            v-for="task in taskPressureRows"
            :key="task.label"
            class="agent-task-row"
          >
            <Avatar
              :icon="task.icon"
              :color="task.severity"
              size="sm"
              no-border
            />
            <div class="min-w-0 flex-1">
              <div>{{ task.label }}</div>
            </div>
            <div class="text-xl font-semibold">{{ task.value }}</div>
          </div>
        </div>
      </section>

      <section class="agent-card agent-card--compact">
        <div class="agent-card__header">
          <div>
            <div class="text-lg font-semibold">{{ kpiMonthTitle }}</div>
            <div class="text-sm text-secondary">{{ kpiMonthSubtitle }}</div>
          </div>
        </div>
        <div class="agent-kpi-month-compact">
          <KpiScoresGrid
            v-model="computedData.kpiScoresGrid"
            view="month"
            :show-headers="false"
            :month-date="selectedDate"
          />
        </div>
      </section>
    </div>
  </div>
  <Empty
    v-else-if="!callCentersAllowed.length"
    title="No call centers"
    description="You haven't been added to any call centers"
    icon="headset"
  />
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue'

// components
import Avatar from '@/components/avatar/Avatar.vue'
import ScoreBadge from '@/components/gamification/ScoreBadge.vue'
import KpiRings from '@/components/dashboard/KpiRings.vue'
import Button from '@/components/button/Button.vue'
import KpiScoresGrid from '@/components/dashboard/widgets/KpiScoresGrid.vue'
import Select from '@/components/forms/Select.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import Icon from '@/components/icon/Icon.vue'

// utilities
import { alertToast } from '@/utilities/notification'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'
import { useTaskStore } from '@/stores/TaskStore.ts'

// composables
import useApiRequest from '@/composables/useApiRequest'

import { useAbility } from '@casl/vue'
import dayjs from 'dayjs'

const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()
const taskStore = useTaskStore()
const { can } = useAbility()

const dashboardData = ref()
const callCentersAllowed = callCenterStore.callCenters

// Users List
const callAgents = ref()
const dataApiRequest = useApiRequest()
const callAgentsRequest = useApiRequest()

/**
 * Computed property to check if dashboard data is loading
 */
const dataIsLoading = computed(() => dataApiRequest.isLoading.value)

const callCenterStorageKey = 'report-filter-call-center-selected'
const agentStorageKey = 'call-agent-dashboard-agent-selected'
const selectedDateStorageKey = 'call-agent-dashboard-selected-date'

const agentSelected = ref(storedNumber(agentStorageKey))
const callCenterSelected = ref(null)

// Selected date for filtering dashboard data
const selectedDate = ref(localStorage.getItem(selectedDateStorageKey) ?? dayjs().startOf('day').format('YYYY-MM-DD'))

/**
 * Computed properties for current month date restrictions
 */
const monthEnd = computed(() => dayjs().endOf('month').format('YYYY-MM-DD'))

const selectedAgentProfile = computed(() => {
  return callAgents.value?.find(agent => agent.id === agentSelected.value)
})

const selectedAgentName = computed(() => {
  return selectedAgentProfile.value?.fullName ?? authStore.details.fullName ?? `${authStore.details.forename} ${authStore.details.surname}`
})

const firstName = computed(() => selectedAgentName.value.split(' ')[0] ?? 'there')

const selectedDateObj = computed(() => dayjs(selectedDate.value))

const selectedDateLongLabel = computed(() => selectedDateObj.value.format('Do MMMM YYYY'))

const selectedDateShortLabel = computed(() => selectedDateObj.value.format('D MMM'))

const selectedDateMonthLabel = computed(() => selectedDateObj.value.format('MMMM YYYY'))

const isSelectedDateToday = computed(() => selectedDateObj.value.isSame(dayjs(), 'day'))

const isSelectedDateCurrentMonth = computed(() => selectedDateObj.value.isSame(dayjs(), 'month'))

const progressSubtitle = computed(() =>
  isSelectedDateToday.value ? 'Productive hours today' : `Productive hours on ${selectedDateLongLabel.value}`
)

const dailyKpiTitle = computed(() => (isSelectedDateToday.value ? 'Daily KPI' : `KPI for ${selectedDateShortLabel.value}`))

const performanceRhythmSubtitle = computed(() =>
  isSelectedDateToday.value ? 'Today, week, and month at a glance' : `${selectedDateLongLabel.value}, week, and month at a glance`
)

const shiftLabel = computed(() => (isSelectedDateToday.value ? 'Shift' : `Shift on ${selectedDateShortLabel.value}`))

const noShiftMessage = computed(() =>
  isSelectedDateToday.value ? 'No shift scheduled today.' : `No shift scheduled on ${selectedDateLongLabel.value}.`
)

const taskPressureSubtitle = computed(() =>
  isSelectedDateToday.value ? 'Open pressure today' : `Open pressure around ${selectedDateShortLabel.value}`
)

const kpiMonthTitle = computed(() => (isSelectedDateCurrentMonth.value ? 'KPI month view' : `KPI ${selectedDateMonthLabel.value}`))

const kpiMonthSubtitle = computed(() =>
  isSelectedDateCurrentMonth.value ? 'Daily score this month' : `Daily score for ${selectedDateMonthLabel.value}`
)

const taskPressureRows = computed(() => [
  {
    label: 'Overdue',
    value: taskCounts.value.overdue,
    severity: 'danger',
    icon: 'triangle-exclamation'
  },
  {
    label: isSelectedDateToday.value ? 'Due Today' : `Due ${selectedDateShortLabel.value}`,
    value: taskCounts.value.dueToday,
    severity: 'warning',
    icon: 'calendar-day'
  },
  {
    label: isSelectedDateToday.value ? 'Due Tomorrow' : `Due ${selectedDateObj.value.add(1, 'day').format('D MMM')}`,
    value: taskCounts.value.dueTomorrow,
    severity: 'brand',
    icon: 'calendar-plus'
  }
])

const selectedDayStatCards = computed(() => [
  {
    label: 'Productive hours',
    value: filteredStats.value.today.productiveHours,
    target: null,
    icon: 'user-clock',
    color: 'brand',
    severity: 'brand'
  },
  {
    label: 'Calls attempted',
    value: filteredStats.value.today.callsAttempted,
    target: `${filteredStats.value.today.callTarget} target`,
    icon: 'phone',
    color: 'info',
    severity: targetSeverity(filteredStats.value.today.callsAttempted, filteredStats.value.today.callTarget)
  },
  {
    label: 'Appointments made',
    value: filteredStats.value.today.appointmentsMade,
    target: `${filteredStats.value.today.appointmentTarget} target`,
    icon: 'calendar',
    color: 'success',
    severity: targetSeverity(filteredStats.value.today.appointmentsMade, filteredStats.value.today.appointmentTarget)
  }
])

const periodPerformanceRows = computed(() => [
  {
    label: dateLabels.value.date,
    productiveHours: filteredStats.value.today.productiveHours,
    calls: filteredStats.value.today.callsAttempted,
    callTarget: filteredStats.value.today.callTarget,
    appointments: filteredStats.value.today.appointmentsMade,
    appointmentTarget: filteredStats.value.today.appointmentTarget
  },
  {
    label: dateLabels.value.week,
    productiveHours: filteredStats.value.week.productiveHours,
    calls: filteredStats.value.week.callsAttempted,
    callTarget: filteredStats.value.week.callTarget,
    appointments: filteredStats.value.week.appointmentsMade,
    appointmentTarget: filteredStats.value.week.appointmentTarget
  },
  {
    label: dateLabels.value.month,
    productiveHours: filteredStats.value.month.productiveHours,
    calls: filteredStats.value.month.callsAttempted,
    callTarget: filteredStats.value.month.callTarget,
    appointments: filteredStats.value.month.appointmentsMade,
    appointmentTarget: filteredStats.value.month.appointmentTarget
  }
])

if (!localStorage.getItem(callCenterStorageKey)) {
  localStorage.setItem(callCenterStorageKey, callCentersAllowed[0]?.id)
  callCenterSelected.value = callCentersAllowed[0]?.id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem(callCenterStorageKey))
}

if (callCenterSelected.value) {
  fetchCallAgents()
}

/**
 * Fetches dashboard data for the selected agent and date.
 * If no agent is selected, uses the logged-in user's ID.
 */
function fetchDashboardData() {
  // Use selected agent or fall back to logged-in user
  const userId = agentSelected.value || authStore.details.id

  if (!userId || !selectedDate.value) {
    return
  }

  dataApiRequest
    .send({
      endpoint: '/v1/dashboards/call-agent',
      params: {
        userId: userId,
        date: dayjs(selectedDate.value).format('YYYY-MM-DD')
      }
    })
    .then(response => {
      dashboardData.value = response.data
    })
}

/**
 * Initialize default agent selection and fetch data
 */
function initializeDashboard() {
  // Set default agent to logged-in user if not a manager or if manager hasn't selected anyone
  if (!can('manage_call_center_team') && authStore.roles.includes(2)) {
    // Non-managers: always use their own ID
    agentSelected.value = authStore.details.id
  } else if (!agentSelected.value && authStore.details.id) {
    // Managers: default to logged-in user if no selection made
    agentSelected.value = authStore.details.id
  }

  persistAgentFilter()

  // Fetch data if we have a user (either selected or default)
  if (agentSelected.value || authStore.details.id) {
    fetchDashboardData()
  }

  // Fetch tasks for the current user
  taskStore.fetchTasks()
}

// Initialize dashboard when component is mounted
onMounted(() => {
  initializeDashboard()
})

function updateCallCenterSelected() {
  localStorage.setItem(callCenterStorageKey, callCenterSelected.value)
  fetchCallAgents()
}

function persistAgentFilter() {
  if (agentSelected.value) {
    localStorage.setItem(agentStorageKey, agentSelected.value)
    return
  }

  localStorage.removeItem(agentStorageKey)
}

function storedNumber(key) {
  const value = Number(localStorage.getItem(key))

  if (Number.isNaN(value) || value === 0) {
    return null
  }

  return value
}

function fetchCallAgents() {
  try {
    callAgentsRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users',
        params: {
          userType: 'all',
          callCenterId: callCenterSelected.value
        }
      })
      .then(response => {
        if (response.success) {
          if (response.data.length == 0) {
            callAgents.value = []
          } else {
            callAgents.value = response.data
            // Order callAgents by name
            callAgents.value?.sort((a, b) => {
              return a.fullName.localeCompare(b.fullName)
            })

            // Keep persisted manager selections only when the selected agent is still in this call centre.
            if (agentSelected.value && !callAgents.value.some(agent => agent.id === agentSelected.value)) {
              agentSelected.value = authStore.details.id
            }
          }
        } else {
          alertToast('Failed to fetch users', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}

/**
 * Handles agent selection change and fetches dashboard data
 */
function onAgentChange() {
  nextTick(() => {
    if (callCenterSelected.value) {
      persistAgentFilter()
      // Fetch data for selected agent, or logged-in user if none selected
      fetchDashboardData()
      // Refresh tasks when agent changes (tasks are always for logged-in user)
      taskStore.fetchTasks()
    } else {
      // Empty the dashboard data if no call center selected
      dashboardData.value = null
    }
  })
}

/**
 * Handles date changes from the date picker.
 * @param {string|string[]} dateValue - The selected date value
 */
function onDateChange(dateValue) {
  // Handle case where dateValue might be an array
  let normalizedDate = dateValue
  if (Array.isArray(dateValue)) {
    normalizedDate = dayjs(dateValue[0]).format('YYYY-MM-DD')
  } else if (dateValue) {
    normalizedDate = dayjs(dateValue).format('YYYY-MM-DD')
  }

  // Update the ref if the normalized date is different
  if (selectedDate.value !== normalizedDate) {
    selectedDate.value = normalizedDate
  }
}

// Watch for date changes (handles v-model updates and normalization)
watch(selectedDate, newDate => {
  // Normalize the date value in case it's an array or different format
  if (Array.isArray(newDate)) {
    const normalizedDate = dayjs(newDate[0]).format('YYYY-MM-DD')
    // Update the ref to normalized value (this won't trigger another watch cycle since value changes)
    if (selectedDate.value !== normalizedDate) {
      selectedDate.value = normalizedDate
    }
    return
  }
  localStorage.setItem(selectedDateStorageKey, selectedDate.value)
  fetchDashboardData()
})

/**
 * Computed property to filter shifts by selected date
 */
const filteredShifts = computed(() => {
  if (!dashboardData.value?.todaysShifts || !selectedDate.value) {
    return []
  }
  return dashboardData.value.todaysShifts.filter(shift => {
    const shiftDate = dayjs(shift.period || shift.date).format('YYYY-MM-DD')
    return shiftDate === selectedDate.value
  })
})

/**
 * Computed property for dynamic date labels
 */
const dateLabels = computed(() => {
  if (!selectedDate.value) {
    return {
      date: 'Today',
      week: 'This Week',
      month: 'This Month'
    }
  }

  const selectedDateObj = dayjs(selectedDate.value)
  const weekStart = selectedDateObj.startOf('isoWeek')
  const weekEnd = selectedDateObj.endOf('isoWeek')
  const isToday = selectedDateObj.isSame(dayjs(), 'day')
  const isCurrentWeek = weekStart.isSame(dayjs().startOf('isoWeek'), 'day')
  const isCurrentMonth = selectedDateObj.isSame(dayjs(), 'month')

  // Format date label
  let dateLabel = selectedDateObj.format('Do MMM YYYY')
  if (isToday) {
    dateLabel = 'Today'
  }

  // Format week label
  let weekLabel = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`
  if (isCurrentWeek) {
    weekLabel = 'This Week'
  } else {
    // Show range from week start to selected date if not full week
    if (!selectedDateObj.isSame(weekEnd, 'day')) {
      weekLabel = `${weekStart.format('MMM D')} - ${selectedDateObj.format('MMM D')}`
    }
  }

  // Format month label
  let monthLabel = selectedDateObj.format('MMMM YYYY')
  if (isCurrentMonth) {
    monthLabel = 'This Month'
  }

  return {
    date: dateLabel,
    week: weekLabel,
    month: monthLabel
  }
})

/**
 * Computed property to calculate filtered stats based on selected date
 */
const filteredStats = computed(() => {
  if (!dashboardData.value || !selectedDate.value) {
    return {
      today: { productiveHours: 0, callsAttempted: 0, appointmentsMade: 0, callTarget: 0, appointmentTarget: 0 },
      week: { productiveHours: 0, callsAttempted: 0, appointmentsMade: 0, callTarget: 0, appointmentTarget: 0 },
      month: { productiveHours: 0, callsAttempted: 0, appointmentsMade: 0, callTarget: 0, appointmentTarget: 0 }
    }
  }

  const selectedDateObj = dayjs(selectedDate.value)
  const weekStart = selectedDateObj.startOf('isoWeek')
  const monthStart = selectedDateObj.startOf('month')

  // Get stats directly from performance array - each day now has stats and targets objects
  const getStatsForDate = date => {
    const dateStr = dayjs(date).format('YYYY-MM-DD')
    const perf = dashboardData.value?.performance?.find(p => p.date === dateStr)

    if (!perf || !perf.stats) {
      return {
        callsInitiated: 0,
        callsAttempted: 0,
        appointmentsMade: 0,
        productiveHours: 0,
        callTarget: 0,
        appointmentTarget: 0
      }
    }

    return {
      callsInitiated: perf.stats.callsInitiated || 0,
      callsAttempted: perf.stats.callsAttempted || 0,
      appointmentsMade: perf.stats.appointmentsMade || 0,
      productiveHours: perf.stats.productiveHours || 0,
      callTarget: perf.targets?.callTarget || 0,
      appointmentTarget: perf.targets?.appointmentTarget || 0
    }
  }

  // Calculate stats for selected date
  const todayStats = getStatsForDate(selectedDate.value)

  // Calculate week stats (sum from week start to selected date)
  const weekStats = { callsAttempted: 0, appointmentsMade: 0, productiveHours: 0, callTarget: 0, appointmentTarget: 0 }
  let currentDate = weekStart.clone()
  while (currentDate.isBefore(selectedDateObj, 'day') || currentDate.isSame(selectedDateObj, 'day')) {
    const stats = getStatsForDate(currentDate)
    weekStats.callsAttempted += stats.callsAttempted
    weekStats.appointmentsMade += stats.appointmentsMade
    weekStats.productiveHours += stats.productiveHours
    weekStats.callTarget += stats.callTarget
    weekStats.appointmentTarget += stats.appointmentTarget
    currentDate = currentDate.add(1, 'day')
  }

  // Calculate month stats (sum from month start to selected date)
  const monthStats = { callsAttempted: 0, appointmentsMade: 0, productiveHours: 0, callTarget: 0, appointmentTarget: 0 }
  currentDate = monthStart.clone()
  while (currentDate.isBefore(selectedDateObj, 'day') || currentDate.isSame(selectedDateObj, 'day')) {
    const stats = getStatsForDate(currentDate)
    monthStats.callsAttempted += stats.callsAttempted
    monthStats.appointmentsMade += stats.appointmentsMade
    monthStats.productiveHours += stats.productiveHours
    monthStats.callTarget += stats.callTarget
    monthStats.appointmentTarget += stats.appointmentTarget
    currentDate = currentDate.add(1, 'day')
  }

  return {
    today: {
      productiveHours: todayStats.productiveHours,
      callsAttempted: todayStats.callsAttempted,
      appointmentsMade: todayStats.appointmentsMade,
      callTarget: todayStats.callTarget,
      appointmentTarget: todayStats.appointmentTarget
    },
    week: {
      productiveHours: weekStats.productiveHours,
      callsAttempted: weekStats.callsAttempted,
      appointmentsMade: weekStats.appointmentsMade,
      callTarget: weekStats.callTarget,
      appointmentTarget: weekStats.appointmentTarget
    },
    month: {
      productiveHours: monthStats.productiveHours,
      callsAttempted: monthStats.callsAttempted,
      appointmentsMade: monthStats.appointmentsMade,
      callTarget: monthStats.callTarget,
      appointmentTarget: monthStats.appointmentTarget
    }
  }
})

/**
 * Computed property to calculate task counts from TaskStore
 * Returns counts for overdue, due on the selected date, and due the next day.
 */
const taskCounts = computed(() => {
  if (!taskStore.tasks || taskStore.tasks.length === 0) {
    return {
      overdue: 0,
      dueToday: 0,
      dueTomorrow: 0
    }
  }

  const selectedDay = selectedDateObj.value.startOf('day')
  const nextDay = selectedDay.add(1, 'day').startOf('day')

  let overdue = 0
  let dueToday = 0
  let dueTomorrow = 0

  taskStore.tasks.forEach(task => {
    // Filter out completed and deleted tasks
    if (task.isCompleted || task.isDeleted) {
      return
    }

    // Must have a valid due date
    if (!task.dueDate || task.dueDate === '0000-00-00 00:00:00') {
      return
    }

    // Check if due date is valid
    const dueDate = dayjs(task.dueDate)
    if (!dueDate.isValid()) {
      return
    }

    const dueDateStart = dueDate.startOf('day')

    // Count overdue tasks before the selected date.
    if (dueDateStart.isBefore(selectedDay)) {
      overdue++
    }
    // Count tasks due on the selected date.
    else if (dueDateStart.isSame(selectedDay)) {
      dueToday++
    }
    // Count tasks due the day after the selected date.
    else if (dueDateStart.isSame(nextDay)) {
      dueTomorrow++
    }
  })

  return {
    overdue,
    dueToday,
    dueTomorrow
  }
})

const computedData = computed(() => {
  let kpiRings = [{ value: 0 }, { value: 0 }, { value: 0 }, { value: 0 }, { value: 0 }]
  const selectedDatePerformance = dashboardData.value?.performance?.find(date => date.date === selectedDate.value)

  if (selectedDatePerformance) {
    kpiRings = [
      { value: selectedDatePerformance.kpi.productivity * 100 },
      { value: selectedDatePerformance.kpi.answerRate * 100 },
      { value: selectedDatePerformance.kpi.opportunityRate * 100 },
      { value: selectedDatePerformance.kpi.conversionRate * 100 },
      { value: selectedDatePerformance.kpi.appVsCallConversion * 100 }
      // { value: selectedDatePerformance.kpi.complianceRate * 100 }
    ]
  }

  let kpiScoresGrid = []
  let streak = 0

  if (dashboardData.value?.performance) {
    kpiScoresGrid = dashboardData.value.performance.map(date => {
      // Get productiveHours from stats to determine if it's a working day
      const productiveHours = date.stats?.productiveHours ?? null
      const isWorkingDay = productiveHours !== null && productiveHours > 0

      return {
        period: date.date,
        productivity: date.kpi.productivity,
        answerRate: date.kpi.answerRate,
        opportunityRate: date.kpi.opportunityRate,
        conversionRate: date.kpi.conversionRate,
        appVsCallConversion: date.kpi.appVsCallConversion,
        score: date.kpi.total !== null ? date.kpi.totalKpisHit : null,
        isWorkingDay: isWorkingDay,
        productiveHours: productiveHours
      }
    })

    /**
     * Calculate streak: Count consecutive working days (from selected date backwards)
     * where all KPIs are hit (score >= 1).
     *
     * Rules:
     * - A working day is defined as a day with productiveHours > 0
     * - Non-working days (productiveHours is null or 0) break the streak
     * - Working days where KPIs are not hit (score < 1 or null) break the streak
     * - Only consecutive working days with score >= 1 count towards the streak
     * - The streak counts backwards from the selected date
     */
    const selectedDateObj = dayjs(selectedDate.value)

    // Filter to selected date and earlier, then sort by date descending (most recent first)
    const daysToCheck = kpiScoresGrid
      .filter(day => {
        const dayDate = dayjs(day.period)
        return !dayDate.isAfter(selectedDateObj, 'day')
      })
      .sort((a, b) => {
        const dateA = dayjs(a.period)
        const dateB = dayjs(b.period)
        return dateB.diff(dateA, 'day') // Descending order (most recent first)
      })

    // Track the last processed working day date to ensure we're checking consecutive working days
    let lastWorkingDayDate = null

    // Loop backwards from selected date
    for (const day of daysToCheck) {
      const dayDate = dayjs(day.period)

      // Check if this is a consecutive working day
      // If we've already processed a working day, ensure this day is the next consecutive day
      if (lastWorkingDayDate !== null) {
        const daysDiff = lastWorkingDayDate.diff(dayDate, 'day')
        // If there's a gap of more than 1 day between working days, break the streak
        // This handles cases where there are non-working days between working days
        if (daysDiff > 1) {
          break
        }
      }

      // If it's not a working day (no productiveHours or productiveHours is 0), break the streak
      if (!day.isWorkingDay) {
        break
      }

      // If it's a working day but score is null (KPIs couldn't be calculated), break the streak
      if (day.score === null) {
        break
      }

      // If it's a working day and all KPIs are hit (score >= 1), increment streak
      if (day.score >= 1) {
        streak++
        lastWorkingDayDate = dayDate
      } else {
        // Working day but KPIs not hit, break the streak
        break
      }
    }
  }

  return {
    kpiRings: kpiRings,
    kpiScoresGrid: kpiScoresGrid,
    badges: {
      streak: streak,
      achievements: 2,
      leaderboard: 3
    }
  }
})

function targetSeverity(value, target) {
  if (!target || value >= target) {
    return 'success'
  }

  if (value >= target * 0.7) {
    return 'warning'
  }

  return 'danger'
}
</script>

<style scoped>
.agent-dashboard {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-height: calc(100vh - var(--s-8));
}

.agent-dashboard__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4);
}

.agent-dashboard__filters {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  justify-content: flex-end;
  gap: var(--s-2);
  flex: 0 0 auto;
  min-width: max-content;
  overflow-x: auto;
  white-space: nowrap;
}

.agent-dashboard__hero {
  display: grid;
  grid-template-columns: minmax(240px, 0.75fr) minmax(320px, 1fr) minmax(300px, 0.9fr);
  gap: var(--s-4);
}

.agent-dashboard__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--s-4);
}

.agent-profile-card,
.agent-overview-card,
.agent-kpi-card,
.agent-card {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.08);
  backdrop-filter: blur(18px);
}

.agent-profile-card {
  position: relative;
  min-height: 21rem;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  padding: var(--s-5);
  color: white;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.1), rgba(15, 23, 42, 0.62)),
    radial-gradient(circle at 50% 14%, rgba(255, 255, 255, 0.28), transparent 11rem),
    linear-gradient(135deg, #fed7aa, #a78bfa);
}

.agent-profile-card__photo {
  position: absolute;
  top: var(--s-5);
  left: var(--s-5);
}

.agent-profile-card__photo :deep(.avatar) {
  width: 6rem !important;
  height: 6rem !important;
  border: 3px solid rgba(255, 255, 255, 0.72);
}

.agent-profile-card__content {
  width: 100%;
}

.agent-profile-card__shifts {
  margin-top: var(--s-5);
  padding-top: var(--s-4);
  border-top: 1px solid rgba(255, 255, 255, 0.24);
}

.agent-profile-shift {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-3);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.14);
}

.agent-profile-shift__bar {
  width: 0.35rem;
  align-self: stretch;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
}

.agent-overview-card,
.agent-kpi-card,
.agent-card {
  padding: var(--s-5);
}

.agent-mini-stat {
  min-width: 0;
  border-radius: 1.25rem;
  padding: var(--s-4);
  background: rgba(255, 255, 255, 0.62);
}

.agent-card--wide {
  grid-column: span 2;
}

.agent-card--compact {
  grid-column: span 1;
}

.agent-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-3);
  margin-bottom: var(--s-4);
}

.agent-period-list {
  display: flex;
  flex-direction: column;
  gap: var(--s-3);
}

.agent-period-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4);
  padding-block: var(--s-3);
  border-bottom: 1px solid var(--border-secondary);
}

.agent-period-row:last-child {
  border-bottom: 0;
}

.agent-period-row__stats {
  display: flex;
  gap: var(--s-5);
  text-align: right;
}

.agent-period-row__stats span {
  display: block;
  font-size: 1.5rem;
  font-weight: 600;
}

.agent-period-row__stats small {
  color: var(--text-secondary);
}

.agent-task-row {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-3);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.62);
}

.agent-kpi-month-compact {
  width: 100%;
}

.agent-kpi-month-compact :deep(.gap-4) {
  gap: 0.35rem;
}

.agent-kpi-month-compact :deep(.mt-2) {
  margin-top: 0;
}

.agent-kpi-month-compact :deep(.pie) {
  max-width: 22px;
  max-height: 22px;
}

.dark .agent-overview-card,
.dark .agent-kpi-card,
.dark .agent-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

.dark .agent-profile-card {
  border-color: rgba(255, 255, 255, 0.1);
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.7)),
    radial-gradient(circle at 50% 14%, rgba(255, 255, 255, 0.12), transparent 11rem),
    linear-gradient(135deg, #7c2d12, #4c1d95);
}

.dark .agent-mini-stat {
  background: rgba(255, 255, 255, 0.06);
}

.dark .agent-task-row {
  background: rgba(255, 255, 255, 0.06);
}

@media (max-width: 1280px) {
  .agent-dashboard__hero,
  .agent-dashboard__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .agent-period-row,
  .agent-period-row__stats {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
  }

  .agent-dashboard__topbar {
    overflow-x: auto;
  }

  .agent-dashboard__hero,
  .agent-dashboard__grid {
    grid-template-columns: 1fr;
  }

  .agent-card--wide {
    grid-column: span 1;
  }
}
</style>
